<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount, computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, EffectCoverflow } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/effect-coverflow'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)
import { getIsScrollLocked } from '@/utils/observerControl'

import { useRequestsStore } from '@/stores/storeRequests'
const storeRequests = useRequestsStore()

const marketList = ref([])
const items = ref([])
const realIndex = ref(0)
let swiperInstance = null
let tl = null
let observer = null
let originalItems = []

async function loadItems() {
	try {
		const data = await storeRequests.getMasks()
		const mapped = data.map((item) => ({
			id: item.id,
			img: item.image,
			title: item.title,
		}))
		originalItems = mapped
		items.value = [...mapped]
	} catch (e) {
		console.error('Ошибка при загрузке масок', e)
	}
}

const isMobile = computed(() => window.innerWidth <= 991.98)

const handleSlideChange = (swiper) => {
	const index = swiper.realIndex
	const total = items.value.length
	const count = 6
	if (window.innerWidth > 991.98) {
		if (index > total - count) {
			const moved = items.value.splice(0, count)
			items.value.push(...moved)
			swiper.update()
			swiper.slideTo(index - count, 0, false)
		}
		if (index < 5) {
			const moved = items.value.splice(-count)
			items.value.unshift(...moved)
			swiper.update()
			swiper.slideTo(index + count, 0, false)
		}
	}

	realIndex.value = swiper.realIndex
}
const onSwiper = (swiper) => {
	swiperInstance = swiper
	const middleIndex = Math.floor(items.value.length / 2)
	swiper.slideToLoop(middleIndex, 0, false)
	realIndex.value = middleIndex
	// setTimeout(() => {
	// 	swiper.loopFix()
	// 	swiper.update()
	// }, 0)
}

onBeforeUnmount(() => {
	if (tl) {
		if (tl.scrollTrigger) tl.scrollTrigger.kill()
		tl.kill()
		tl = null
	}
	if (observer) observer.disconnect()
})

onMounted(async () => {
	const res = await storeRequests.getMarketPlaces()
	// фильтруем только те, где есть url
	marketList.value = res.filter((item) => !!item.url)
	await loadItems()
})
</script>

<template>
	<section class="moore-looks" id="masks">
		<div class="moore-looks__screen moore-looks__screen-one">
			<div class="moore-looks__bg">
				<img loading="lazy" src="@/assets/img/page_bg-second.jpg" alt="" />
			</div>

			<div class="moore-looks__img">
				<picture>
					<source
						srcset="@/assets/img/moore-looks/moore-looks_bg.png"
						media="(min-width: 991.98px)"
					/>
					<img
						loading="lazy"
						src="@/assets/img/moore-looks/moore-looks_bg--min.png"
						alt=""
					/>
				</picture>
			</div>

			<div class="moore-looks__center">
				<div class="moore-looks__center_img">
					<img loading="lazy" src="@/assets/img/moore-looks/moore-looks_img.png" alt="" />
				</div>
				<div class="moore-looks__center_text">
					<img
						loading="lazy"
						src="@/assets/img/moore-looks/moore-looks_text.png"
						alt=""
					/>
				</div>
			</div>
		</div>

		<div class="moore-looks__screen moore-looks__screen-two">
			<div class="moore-looks__bg">
				<img loading="lazy" src="@/assets/img/page_bg-primary.jpg" alt="" />
			</div>

			<!-- :looped-slides="items.length" 	:centeredSlides="false" :loop-additional-slides="items.length"-->
			<div class="moore-looks__block-wrap">
				<div class="moore-looks__container">
					<div class="moore-looks__title">
						<h1>Более 500 образов</h1>
					</div>
				</div>

				<div class="moore-looks__block">
					<Swiper
						v-if="items.length"
						class="moore-looks__slider"
						:modules="[Navigation, EffectCoverflow]"
						effect="coverflow"
						:grabCursor="true"
						slides-per-view="auto"
						:centeredSlides="true"
						:space-between="0"
						:loop="isMobile"
						:observer="true"
						:observe-parents="true"
						:navigation="{
							prevEl: '.moore-looks__button-prev',
							nextEl: '.moore-looks__button-next',
						}"
						:coverflowEffect="{
							rotate: 0,
							stretch: 250,
							depth: 250,
							modifier: 1,
							slideShadows: false,
						}"
						:breakpoints="{
							0: {
								coverflowEffect: {
									rotate: 0,
									stretch: 150,
									depth: 150,
									modifier: 1,
									slideShadows: false,
								},
							},
							991.98: {
								coverflowEffect: {
									rotate: 0,
									stretch: 250,
									depth: 250,
									modifier: 1,
									slideShadows: false,
								},
							},
						}"
						@swiper="onSwiper"
						@slideChange="handleSlideChange"
					>
						<SwiperSlide
							v-for="item in items"
							:key="item.id"
							class="moore-looks__slide"
						>
							<img :src="item.img" :alt="item.title" />
						</SwiperSlide>
					</Swiper>

					<div class="moore-looks__footer">
						<div class="moore-looks__actions">
							<button type="button" class="arrow-btn moore-looks__button-prev">
								<svg>
									<use :href="`#arrow-left`" />
								</svg>
							</button>
							<div class="moore-looks__desc">
								<transition name="fade" mode="out-in">
									<h6 :key="items[realIndex]?.id">
										{{ items[realIndex]?.title }}
									</h6>
								</transition>
							</div>
							<button type="button" class="arrow-btn moore-looks__button-next">
								<svg>
									<use :href="`#arrow-right`" />
								</svg>
							</button>
						</div>
						<ul class="moore-looks__links">
							<li v-for="item in marketList" :key="item.id">
								<a :href="item.url" target="_blank" rel="noopener">
									<img :src="item.logo" :alt="item.name" />
								</a>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
.moore-looks {
	position: relative;
	overflow: hidden;

	&__screen {
		position: relative;

		&-one {
			height: 100vh;

			@media (min-width: 991.98px) and (max-height: 899.98px) {
				min-height: 89.998rem;
			}

			@media (max-width: 991.98px) and (max-height: 599.98px) {
				min-height: 59.998rem;
			}
		}

		&-two {
			padding: 5rem 0;
		}
	}

	// .moore-looks__bg
	&__bg {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		z-index: -1;
		pointer-events: none;
		user-select: none;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	// .moore-looks__img
	&__img {
		position: absolute;
		left: 50%;
		top: 50%;
		width: 100%;
		height: 100%;
		transform: translate(-50%, -50%);
		z-index: 0;
		pointer-events: none;
		user-select: none;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	// .moore-looks__center
	&__center {
		width: 43.3rem;
		height: 43.3rem;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		pointer-events: none;
		user-select: none;

		@media (max-width: 991.98px) {
			width: 14.2rem;
			height: 14.2rem;
		}

		// .moore-looks__center_img
		&_img {
			width: 17.4rem;
			height: 17.4rem;
			aspect-ratio: 1/1;
			border-radius: 50%;
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			z-index: 10;

			@media (max-width: 991.98px) {
				width: 6.7rem;
				height: 6.7rem;
			}

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		// .moore-looks__center_text
		&_text {
			width: 100%;
			height: 100%;

			img {
				width: 100%;
				height: 100%;
				object-fit: contain;
			}
		}
	}

	&__block-wrap {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
	}

	// .moore-looks__title
	&__title {
		text-align: center;
		color: #000;
	}

	// .moore-looks__block
	&__block {
	}

	// .moore-looks__slider
	&__slider {
		overflow: visible;
		max-width: 60rem;
	}

	// .moore-looks__slide
	&__slide {
		width: 100%;
		max-width: 60rem;

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	// .moore-looks__footer
	&__footer {
	}

	// .moore-looks__actions
	&__actions {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 3.5rem;
	}

	// .moore-looks__button-prev
	&__button-prev,
	&__button-next {
		color: #101010;

		svg {
			width: 6.2rem;
			height: 4.8rem;

			@media (max-width: 991.98px) {
				width: 4.4rem;
				height: 5rem;
			}
		}
	}

	// .moore-looks__desc
	&__desc {
		min-height: 2em;
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: 28rem;
		font-weight: 400;
		font-size: 3.6rem;
		// line-height: 4.2rem;
		line-height: 116%;
		letter-spacing: 0.04em;
		text-transform: lowercase;
		text-align: center;
		color: #000;

		@media (max-width: 991.98px) {
			min-width: unset;
			flex: 1 1 auto;
			font-size: 2.5rem;
		}
	}

	// .moore-looks__links
	&__links {
		margin-top: 1.4rem;
		display: flex;
		align-items: center;
		justify-content: center;

		@media (max-width: 991.98px) {
			margin-top: 0.8rem;
		}

		li {
			a {
				display: block;
				width: 8rem;
				height: 8rem;

				@media (max-width: 991.98px) {
					width: 7.2rem;
					height: 7.2rem;
				}

				svg {
					width: 100%;
					height: 100%;
				}
			}
		}
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
